import { NHOV_BRANCH_IDS } from "../nHov/nHovBranches";
import {
  HOV_AMPLIFIER_ENTRY_IDS,
  type HovAmplifierBurnState,
  type HovAmplifierEntryId,
  type HovAmplifierSnapshot,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import type { HovSkillId } from "@/features/hoverIdle/hov/hovSkillDefinitions";
import type { HovSkillResolvedEffects } from "@/features/hoverIdle/hov/hovSkillEffects";
import type { HovGainUpgradeMpTarget } from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import {
  MPM_MHOV_EXECUTION_MP_COST,
  MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  MPM_OHOV_EXECUTION_MP_COST,
} from "@/features/hoverIdle/mpm/mpmState";
import type {
  HoverIdleSnapshot,
  NHovBranchId,
} from "@/features/hoverIdle/types";
import type { WallRewardEffect } from "@/features/hoverIdle/wall/wallChallengePolicy";
import type { WallId } from "@/features/hoverIdle/wall/wallDefinitions";
import type { WallSnapshot } from "@/features/hoverIdle/wall/wallSnapshot";
import { compareHuge, serializeHuge, type HugeNumber } from "@/shared/numbers";

// Compact balance-state summaries built from the authoritative model
// snapshots. These shapes are stable PlayLog contract: extractors and balance
// analysis scripts read them by field name. Keep new analysis fields purely
// additive so the existing OHov1 summary script and downstream extractors do
// not break.

export interface PlayLogHovSkillEffectsSummary {
  readonly hov1GainMultiplier: string;
  readonly hov1GainMultiplierLog10: number | null;
  readonly hovGainPerSecond: string;
  readonly hovGainPerSecondLog10: number | null;
  readonly timGainPerSecond: string;
  readonly timGainPerSecondLog10: number | null;
  readonly mpCapacityBonus: string;
  readonly bhgToHgPowerExponentBonus: number;
  readonly hgHngSimultaneousPurchaseCountBaseBonus: number;
  readonly hngRankScaleBaseBonus: number;
  readonly amplifierUnlocked: boolean;
  readonly amplifierPanelMpPlacementUnlocked: boolean;
  readonly mhovMpExecutionUnlocked: boolean;
  readonly nhovMpExecutionAssignmentUnlocked: boolean;
  readonly ohovHovCoreGainMultiplierUnlocked: boolean;
  readonly mpCapacityCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly freeMpCoreOHovHovCoreGainMultiplierUnlocked: boolean;
  readonly wallUnlocked: boolean;
  readonly explorationUnlocked: boolean;
  readonly wallRewardCapRelaxed: boolean;
  readonly amplifierGainOHovMultiplierTargetEntries: readonly HovAmplifierEntryId[];
  readonly amplifierGainClearedWallKindMultiplierTargetEntries: readonly HovAmplifierEntryId[];
  readonly amplifierGainOHovRunElapsedMultiplierTargetEntries: readonly HovAmplifierEntryId[];
  readonly amplifierGainFixedMultipliersByEntry: Partial<
    Record<HovAmplifierEntryId, string>
  >;
}

export interface PlayLogHovSkillSummary {
  readonly acquiredCount: number;
  readonly acquiredIds: readonly HovSkillId[];
  readonly effects: PlayLogHovSkillEffectsSummary;
}

export interface PlayLogAmplifierEntrySummary {
  readonly id: HovAmplifierEntryId;
  readonly unlocked: boolean;
  readonly enhancementLevel: number;
  readonly gaugeValue: number;
  readonly isDraining: boolean;
  readonly burnState: HovAmplifierBurnState;
  readonly amplifierGainMultiplier: string;
  readonly amplifierGainMultiplierLog10: number | null;
  readonly effectiveAmplifierGainPerSecond: string;
  readonly effectiveAmplifierGainPerSecondLog10: number | null;
  readonly activeGaugeOutputGain: string;
  readonly activeGaugeOutputGainLog10: number | null;
}

export interface PlayLogAmplifierSummary {
  readonly unlocked: boolean;
  readonly ap: string;
  readonly apLog10: number | null;
  readonly apExponent: number;
  readonly hgMultiplier: string;
  readonly hgMultiplierLog10: number | null;
  readonly unlockedEntryIds: readonly HovAmplifierEntryId[];
  readonly activeEntryIds: readonly HovAmplifierEntryId[];
  readonly entries: readonly PlayLogAmplifierEntrySummary[];
}

export interface PlayLogWallEntrySummary {
  readonly id: WallId;
  readonly active: boolean;
  readonly challengeAvailable: boolean;
  readonly cleared: boolean;
  readonly bestClearMs: number | null;
  // Authoritative typed reward effect mirror. Carries the additive value for
  // walls whose reward composes additively (e.g. Freshness Wall MHov exponent
  // bonus) so balance analysis can read the true reward shape rather than a
  // multiplier projection.
  readonly rewardEffect: WallRewardEffect;
  // Legacy multiplier projection kept for back-compat with existing balance
  // extractors. Equals `rewardEffect.value` for multiply effects and 1 for
  // add effects; never the "1 + bonus" display fiction.
  readonly rewardMultiplier: number;
}

export interface PlayLogWallSummary {
  readonly unlocked: boolean;
  readonly activeWallId: WallId | null;
  readonly activeRunElapsedMs: number | null;
  readonly clearedWallIds: readonly WallId[];
  readonly walls: readonly PlayLogWallEntrySummary[];
}

export interface PlayLogMpAssignmentsSummary {
  readonly hovTargets: number;
  readonly hovGainUpgrades: number;
  readonly amplifierPanels: number;
  readonly amplifierUpgrades: number;
  readonly mhovExecutionMpCost: number;
  readonly nhovExecutionAssignments: number;
  readonly nhovExecutionMpCost: number;
  readonly ohovExecutionMpCost: number;
  readonly support: Partial<Record<string, number>>;
}

export interface PlayLogMpSectionsSummary {
  readonly hovTargetsUnlocked: boolean;
  readonly hovTargetsEnabled: boolean;
  readonly hovGainUpgradesUnlocked: boolean;
  readonly hovGainUpgradesEnabled: boolean;
  readonly amplifierPanelsUnlocked: boolean;
  readonly amplifierPanelsEnabled: boolean;
  readonly amplifierUpgradesUnlocked: boolean;
  readonly amplifierUpgradesEnabled: boolean;
  readonly mhovExecutionUnlocked: boolean;
  readonly mhovExecutionEnabled: boolean;
  readonly mhovExecutionAssigned: boolean;
  readonly nhovExecutionUnlocked: boolean;
  readonly nhovExecutionEnabled: boolean;
  readonly ohovExecutionUnlocked: boolean;
  readonly ohovExecutionEnabled: boolean;
  readonly ohovExecutionAssigned: boolean;
  readonly assignAllUnlocked: boolean;
}

export interface PlayLogMpNumericSettings {
  readonly mhovExecution: {
    readonly multiplier: string;
    readonly minimumResetIntervalMs: number;
  };
  readonly nhovExecution: {
    readonly conditions: Readonly<
      Record<
        NHovBranchId,
        {
          readonly minimumEffect: string;
          readonly minimumTimeSeconds: number;
        }
      >
    >;
  };
  readonly ohovExecution: {
    readonly minimumHovCore: string;
    readonly minimumIntervalMs: number;
  };
}

export interface PlayLogMpSummary {
  // Absent in historical logs; new snapshot builders always include it.
  readonly numericSettings?: PlayLogMpNumericSettings;
  readonly capacity: number;
  readonly assignedTotal: number;
  readonly available: number;
  readonly assignments: PlayLogMpAssignmentsSummary;
  readonly sections: PlayLogMpSectionsSummary;
  readonly hovTargetCellIndices: readonly number[];
  readonly hovGainUpgradeTargets: readonly string[];
  readonly amplifierPanelEntryIds: readonly HovAmplifierEntryId[];
  readonly amplifierUpgradeEntryIds: readonly HovAmplifierEntryId[];
  readonly nhovExecutionBranchIds: readonly string[];
}

export interface BuildPlayLogHovSkillSummaryInput {
  readonly acquired: ReadonlySet<HovSkillId> | readonly HovSkillId[];
  readonly resolvedEffects: HovSkillResolvedEffects;
}

export function buildPlayLogHovSkillSummary(
  input: BuildPlayLogHovSkillSummaryInput,
): PlayLogHovSkillSummary {
  const acquiredIds = toSortedSkillIds(input.acquired);
  const effects = input.resolvedEffects;
  const fixedMultipliersByEntry: Partial<Record<HovAmplifierEntryId, string>> =
    {};
  for (const [
    entryId,
    multiplier,
  ] of effects.amplifierGainFixedMultipliersByEntry) {
    fixedMultipliersByEntry[entryId] = serializeHuge(multiplier);
  }

  return {
    acquiredCount: acquiredIds.length,
    acquiredIds,
    effects: {
      hov1GainMultiplier: serializeHuge(effects.hov1GainMultiplier),
      hov1GainMultiplierLog10: toSafeLog10(effects.hov1GainMultiplier),
      hovGainPerSecond: serializeHuge(effects.hovGainPerSecond),
      hovGainPerSecondLog10: toSafeLog10(effects.hovGainPerSecond),
      timGainPerSecond: serializeHuge(effects.timGainPerSecond),
      timGainPerSecondLog10: toSafeLog10(effects.timGainPerSecond),
      mpCapacityBonus: serializeHuge(effects.mpCapacity),
      bhgToHgPowerExponentBonus: effects.bhgToHgPowerExponentBonus,
      hgHngSimultaneousPurchaseCountBaseBonus:
        effects.hgHngSimultaneousPurchaseCountBaseBonus,
      hngRankScaleBaseBonus: effects.hngRankScaleBaseBonus,
      amplifierUnlocked: effects.amplifierUnlocked,
      amplifierPanelMpPlacementUnlocked:
        effects.amplifierPanelMpPlacementUnlocked,
      mhovMpExecutionUnlocked: effects.mhovMpExecutionUnlocked,
      nhovMpExecutionAssignmentUnlocked:
        effects.nhovMpExecutionAssignmentUnlocked,
      ohovHovCoreGainMultiplierUnlocked:
        effects.ohovHovCoreGainMultiplierUnlocked,
      mpCapacityCoreOHovHovCoreGainMultiplierUnlocked:
        effects.mpCapacityCoreOHovHovCoreGainMultiplierUnlocked,
      freeMpCoreOHovHovCoreGainMultiplierUnlocked:
        effects.freeMpCoreOHovHovCoreGainMultiplierUnlocked,
      wallUnlocked: effects.wallUnlocked,
      explorationUnlocked: effects.explorationUnlocked,
      wallRewardCapRelaxed: effects.wallRewardCapRelaxed,
      amplifierGainOHovMultiplierTargetEntries: toSortedEntryIds(
        effects.amplifierGainOHovMultiplierTargets,
      ),
      amplifierGainClearedWallKindMultiplierTargetEntries: toSortedEntryIds(
        effects.amplifierGainClearedWallKindMultiplierTargets,
      ),
      amplifierGainOHovRunElapsedMultiplierTargetEntries: toSortedEntryIds(
        effects.amplifierGainOHovRunElapsedMultiplierTargets,
      ),
      amplifierGainFixedMultipliersByEntry: fixedMultipliersByEntry,
    },
  };
}

export function buildPlayLogAmplifierSummary(
  snapshot: HovAmplifierSnapshot,
): PlayLogAmplifierSummary {
  const entries = snapshot.entries.map<PlayLogAmplifierEntrySummary>(
    (entry) => ({
      id: entry.id,
      unlocked: entry.unlocked,
      enhancementLevel: entry.enhancementLevel,
      gaugeValue: clampUnitInterval(entry.gaugeValue),
      isDraining: entry.isDraining,
      burnState: entry.burnState,
      amplifierGainMultiplier: serializeHuge(entry.amplifierGainMultiplier),
      amplifierGainMultiplierLog10: toSafeLog10(entry.amplifierGainMultiplier),
      effectiveAmplifierGainPerSecond: serializeHuge(
        entry.effectiveAmplifierGainPerSecond,
      ),
      effectiveAmplifierGainPerSecondLog10: toSafeLog10(
        entry.effectiveAmplifierGainPerSecond,
      ),
      activeGaugeOutputGain: serializeHuge(entry.activeGaugeOutputGain),
      activeGaugeOutputGainLog10: toSafeLog10(entry.activeGaugeOutputGain),
    }),
  );

  const unlockedEntryIds = entries
    .filter((entry) => entry.unlocked)
    .map((entry) => entry.id);
  const activeEntryIds = entries
    .filter((entry) => entry.unlocked && entry.isDraining)
    .map((entry) => entry.id);

  // RA's amplifier multiplier moves with AP changes, so treat the existence
  // of an unlocked RA entry with non-zero AP as the "amplifier system is
  // unlocked" signal. The skill snapshot owns the canonical unlock flag, but
  // PlayLog needs an answer even when the skill summary isn't passed in.
  const unlocked = unlockedEntryIds.length > 0;

  return {
    unlocked,
    ap: serializeHuge(snapshot.ap),
    apLog10: toSafeLog10(snapshot.ap),
    apExponent: snapshot.apExponent,
    hgMultiplier: serializeHuge(snapshot.hgMultiplier),
    hgMultiplierLog10: toSafeLog10(snapshot.hgMultiplier),
    unlockedEntryIds,
    activeEntryIds,
    entries,
  };
}

export function buildPlayLogWallSummary(
  snapshot: WallSnapshot,
): PlayLogWallSummary {
  const walls = snapshot.walls.map<PlayLogWallEntrySummary>((wall) => ({
    id: wall.id,
    active: wall.active,
    challengeAvailable: wall.challengeAvailable,
    cleared: wall.bestClearMs !== null,
    bestClearMs: wall.bestClearMs,
    rewardEffect: wall.rewardEffect,
    rewardMultiplier: wall.rewardMultiplier,
  }));
  const clearedWallIds = walls
    .filter((wall) => wall.cleared)
    .map((wall) => wall.id);
  const activeRunElapsedMs =
    snapshot.walls.find((wall) => wall.active)?.currentRunElapsedMs ?? null;

  return {
    unlocked: snapshot.wallUnlocked,
    activeWallId: snapshot.activeWallId,
    activeRunElapsedMs,
    clearedWallIds,
    walls,
  };
}

export function buildPlayLogMpSummary(
  snapshot: HoverIdleSnapshot,
): PlayLogMpSummary {
  const mpm = snapshot.mpm;
  const capacity = snapshot.mpCapacity;
  const hovTargetsAssigned = mpm.hovTargets.assignments.length;
  const hovGainUpgradesAssigned = mpm.hovGainUpgrades.assignments.length;
  const amplifierPanelsAssigned = mpm.amplifierPanels.assignments.length;
  const amplifierUpgradesAssigned = mpm.amplifierUpgrades.assignments.length;
  const mhovExecutionAssigned = mpm.mhovExecution.assigned;
  const mhovExecutionMpCost = mhovExecutionAssigned
    ? MPM_MHOV_EXECUTION_MP_COST
    : 0;
  const nhovExecutionAssignments = mpm.nhovExecution.assignments.length;
  const nhovExecutionMpCost =
    nhovExecutionAssignments * MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH;
  const ohovExecutionAssigned = mpm.ohovExecution.assigned;
  const ohovExecutionMpCost = ohovExecutionAssigned
    ? MPM_OHOV_EXECUTION_MP_COST
    : 0;
  const supportAssignments: Partial<Record<string, number>> = {};
  for (const [assignmentId, count] of Object.entries(mpm.assignments)) {
    if (count > 0) {
      supportAssignments[assignmentId] = count;
    }
  }
  const assignedTotal =
    hovTargetsAssigned +
    hovGainUpgradesAssigned +
    amplifierPanelsAssigned +
    amplifierUpgradesAssigned +
    mhovExecutionMpCost +
    nhovExecutionMpCost +
    ohovExecutionMpCost;
  const available = Math.max(0, capacity - assignedTotal);

  return {
    capacity,
    assignedTotal,
    available,
    assignments: {
      hovTargets: hovTargetsAssigned,
      hovGainUpgrades: hovGainUpgradesAssigned,
      amplifierPanels: amplifierPanelsAssigned,
      amplifierUpgrades: amplifierUpgradesAssigned,
      mhovExecutionMpCost,
      nhovExecutionAssignments,
      nhovExecutionMpCost,
      ohovExecutionMpCost,
      support: supportAssignments,
    },
    sections: {
      hovTargetsUnlocked: mpm.hovTargets.unlocked,
      hovTargetsEnabled: mpm.hovTargets.enabled,
      hovGainUpgradesUnlocked: mpm.hovGainUpgrades.unlocked,
      hovGainUpgradesEnabled: mpm.hovGainUpgrades.enabled,
      amplifierPanelsUnlocked: mpm.amplifierPanels.unlocked,
      amplifierPanelsEnabled: mpm.amplifierPanels.enabled,
      amplifierUpgradesUnlocked: mpm.amplifierUpgrades.unlocked,
      amplifierUpgradesEnabled: mpm.amplifierUpgrades.enabled,
      mhovExecutionUnlocked: mpm.mhovExecution.unlocked,
      mhovExecutionEnabled: mpm.mhovExecution.enabled,
      mhovExecutionAssigned,
      nhovExecutionUnlocked: mpm.nhovExecution.unlocked,
      nhovExecutionEnabled: mpm.nhovExecution.enabled,
      ohovExecutionUnlocked: mpm.ohovExecution.unlocked,
      ohovExecutionEnabled: mpm.ohovExecution.enabled,
      ohovExecutionAssigned,
      assignAllUnlocked: mpm.assignAllUnlocked,
    },
    hovTargetCellIndices: [...mpm.hovTargets.assignments],
    hovGainUpgradeTargets: mpm.hovGainUpgrades.assignments.map(
      formatHovGainUpgradeMpTarget,
    ),
    amplifierPanelEntryIds: [...mpm.amplifierPanels.assignments],
    amplifierUpgradeEntryIds: [...mpm.amplifierUpgrades.assignments],
    nhovExecutionBranchIds: [...mpm.nhovExecution.assignments],
    numericSettings: {
      mhovExecution: {
        multiplier: serializeHuge(mpm.mhovExecution.multiplier),
        minimumResetIntervalMs: mpm.mhovExecution.minimumResetIntervalMs,
      },
      nhovExecution: {
        conditions: Object.fromEntries(
          NHOV_BRANCH_IDS.map((branchId) => {
            const condition = mpm.nhovExecution.conditions[branchId];
            return [
              branchId,
              {
                minimumEffect: serializeHuge(condition.minimumEffect),
                minimumTimeSeconds: condition.minimumTimeSeconds,
              },
            ];
          }),
        ) as PlayLogMpNumericSettings["nhovExecution"]["conditions"],
      },
      ohovExecution: {
        minimumHovCore: serializeHuge(mpm.ohovExecution.minimumHovCore),
        minimumIntervalMs: mpm.ohovExecution.minimumIntervalMs,
      },
    },
  };
}

function toSortedSkillIds(
  source: ReadonlySet<HovSkillId> | readonly HovSkillId[],
): HovSkillId[] {
  const ids = Array.isArray(source) ? [...source] : [...source];
  ids.sort();
  return ids;
}

function toSortedEntryIds(
  source: ReadonlySet<HovAmplifierEntryId>,
): HovAmplifierEntryId[] {
  const order = new Map<HovAmplifierEntryId, number>();
  HOV_AMPLIFIER_ENTRY_IDS.forEach((id, index) => order.set(id, index));
  return [...source].sort(
    (a, b) =>
      (order.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b) ?? Number.MAX_SAFE_INTEGER),
  );
}

function clampUnitInterval(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function formatHovGainUpgradeMpTarget(target: HovGainUpgradeMpTarget): string {
  if (target === "global") return "global";
  return `cell:${target.index}`;
}

function toSafeLog10(value: HugeNumber): number | null {
  if (!value.isFinite() || compareHuge(value, 0) <= 0) {
    return null;
  }

  try {
    const result = value.log10().toNumber();
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}
